/**
 * Animation renderer for custom keyframe animations
 */

interface AnimationKeyframe {
  percent: number;
  x?: number;
  y?: number;
  scale?: number;
  rotation?: number;
  opacity?: number;
}

interface AnimationConfig {
  keyframes: AnimationKeyframe[];
  duration: number;
  delay: number;
  easing: string;
  loop: boolean;
  reverse: boolean;
}

const getEasing = (easing: string): string => {
  switch (easing) {
    case 'bounce': return 'cubic-bezier(0.34, 1.56, 0.64, 1)';
    case 'elastic': return 'cubic-bezier(0.68, -0.55, 0.27, 1.55)';
    default: return easing;
  }
};

export function generateCustomKeyframes(
  elementId: string,
  config: AnimationConfig,
  movementOffset?: { x: number; y: number }
): { keyframeName: string; css: string; animationValue: string } {
  const keyframeName = `custom-anim-${elementId}`;

  let maxKfX = 0;
  let maxKfY = 0;
  for (const kf of config.keyframes) {
    if (kf.x !== undefined && Math.abs(kf.x) > Math.abs(maxKfX)) maxKfX = kf.x;
    if (kf.y !== undefined && Math.abs(kf.y) > Math.abs(maxKfY)) maxKfY = kf.y;
  }

  const scaleX = (movementOffset && maxKfX !== 0) ? movementOffset.x / maxKfX : 1;
  const scaleY = (movementOffset && maxKfY !== 0) ? movementOffset.y / maxKfY : 1;

  const sorted = [...config.keyframes].sort((a, b) => a.percent - b.percent);

  const frames = sorted.map(kf => {
    const transforms: string[] = [];
    if (kf.x !== undefined) transforms.push(`translateX(${kf.x * scaleX}px)`);
    if (kf.y !== undefined) transforms.push(`translateY(${kf.y * scaleY}px)`);
    if (kf.scale !== undefined) transforms.push(`scale(${kf.scale})`);
    if (kf.rotation !== undefined) transforms.push(`rotate(${kf.rotation}deg)`);

    const props: string[] = [];
    if (transforms.length > 0) props.push(`transform: ${transforms.join(' ')}`);
    if (kf.opacity !== undefined) props.push(`opacity: ${kf.opacity}`);

    return `  ${kf.percent}% { ${props.join('; ')}; }`;
  }).join('\n');

  const css = `@keyframes ${keyframeName} {\n${frames}\n}`;

  const direction = config.reverse ? 'alternate' : 'normal';
  const iterations = config.loop ? 'infinite' : '1';
  const easing = getEasing(config.easing);
  const fillMode = config.loop ? 'none' : 'forwards';

  const animationValue = `${keyframeName} ${config.duration}ms ${easing} ${config.delay}ms ${iterations} ${direction} ${fillMode}`;

  return { keyframeName, css, animationValue };
}
